import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Calendar } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import {
  PAYMENT_AMOUNT_TYPE_ENUM,
  PAYMENT_AMOUNT_TYPE_LIST,
  PAYMENT_DUE_TYPE_ENUM,
  PAYMENT_DUE_TYPE_LIST
} from '../../../config/constants';
import AppText from '../../common/AppText';
import CustomNumberInput from '../../common/CustomNumberInput';

const PaymentPlanAddInstallmentModal = ({
  isOpen,
  onClose,
  onConfirm,
  installments,
  installment
}) => {
  const [formState, setFormState] = useState({
    amount: 2500,
    dueValue: 1,
    fixedDueDate: null,
    fkPaymentAmountType: null,
    fkPaymentDueType: null,
    errors: {}
  });

  const isFirstInstallment = installment
    ? installment.sort === 1
    : installments?.length === 0;

  useEffect(() => {
    if (isOpen) {
      if (installment) {
        setFormState({
          amount: installment.amount,
          dueValue: installment.dueValue,
          fixedDueDate: installment.fixedDueDate,
          fkPaymentAmountType: installment.fkPaymentAmountType.toString(),
          fkPaymentDueType: installment.fkPaymentDueType.toString(),
          errors: {}
        });
      }
      else {
        setFormState({
          amount: 2500,
          dueValue: 1,
          fixedDueDate: null,
          fkPaymentAmountType: PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE,
          fkPaymentDueType: isFirstInstallment
            ? PAYMENT_DUE_TYPE_ENUM.IMMEDIATE
            : PAYMENT_DUE_TYPE_ENUM.MONTHS_AFTER_PREVIOUS,
          errors: {}
        });
      }
    }
  }, [isOpen]);

  const onBlurAmountInput = () => {
    const amountError =
      formState.fkPaymentAmountType === PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE &&
      formState.amount < 100
        ? 'Amount must be at least 1%'
        : formState.fkPaymentAmountType === PAYMENT_AMOUNT_TYPE_ENUM.FIXED &&
          formState.amount < 1000
        ? 'Amount must be at least $10'
        : null;

    if (amountError) {
      setFormState({
        ...formState,
        errors: {
          ...formState.errors,
          amount: amountError
        }
      });
    }
  };

  return (
    <ResponsiveModal
      formSectionProps={{
        onSubmit: () => {
          onConfirm(formState);
        },
        onCancel: onClose,
        isSubmitDisabled: Object.values(formState.errors || {}).some(
          (v) => !!v
        ),
        submitTitle: 'Confirm'
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      title="Add installment"
    >
      <AppStack style={{ gap: 16 }}>
        <AppStack style={{ gap: 5 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Payment amount
          </AppText>

          <AppFlexbox
            style={{
              flex: 1,
              gap: 8,
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}
          >
            {formState.fkPaymentAmountType ===
            PAYMENT_AMOUNT_TYPE_ENUM.REMAINING_BALANCE ? (
              <CustomNumberInput
                allowNegative={false}
                disabled
                hideControls
                leftSection={
                  <AppText
                    style={{
                      fontWeight: 500,
                      color: '#666',
                      fontSize: 14
                    }}
                  >
                    $
                  </AppText>
                }
                placeholder="---"
                required
                style={{ flex: 1 }}
              />
            ) : formState.fkPaymentAmountType ===
              PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE ? (
              <CustomNumberInput
                allowNegative={false}
                decimalScale={0}
                error={formState.errors?.amount}
                fixedDecimalScale
                max={100}
                min={0}
                onBlur={onBlurAmountInput}
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    amount: v * 100,
                    errors: {
                      ...formState.errors,
                      amount: null
                    }
                  });
                }}
                placeholder="25"
                required
                rightSection={
                  <AppText
                    style={{
                      fontWeight: 500,
                      color: '#666',
                      fontSize: 14
                    }}
                  >
                    %
                  </AppText>
                }
                style={{ flex: 1 }}
                value={formState.amount / 100}
                withAsterisk={false}
              />
            ) : (
              <CustomNumberInput
                allowNegative={false}
                decimalScale={2}
                error={formState.errors?.amount}
                fixedDecimalScale
                leftSection={
                  <AppText
                    style={{
                      fontWeight: 500,
                      color: '#666',
                      fontSize: 14
                    }}
                  >
                    $
                  </AppText>
                }
                min={0}
                onBlur={onBlurAmountInput}
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    amount: v * 100,
                    errors: {
                      ...formState.errors,
                      amount: null
                    }
                  });
                }}
                placeholder="25"
                required
                style={{ flex: 1 }}
                value={formState.amount / 100}
                withAsterisk={false}
              />
            )}

            <Select
              data={PAYMENT_AMOUNT_TYPE_LIST.filter((item) =>
                isFirstInstallment
                  ? !item.hideOnFirstInstallment
                  : true &&
                    (!item.isLimitedToOneInstallment ||
                      installments?.find(
                        (i) => i.fkPaymentAmountType === item.value
                      ) === undefined)
              )}
              onChange={(v) => {
                if (v) {
                  setFormState({
                    ...formState,
                    fkPaymentAmountType: v,
                    amount:
                      v === PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE
                        ? Math.floor(formState.amount / 100) * 100
                        : formState.amount,
                    errors: {
                      ...formState.errors,
                      amount:
                        v === PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE &&
                        formState.amount < 100
                          ? 'Amount must be at least 1%'
                          : v === PAYMENT_AMOUNT_TYPE_ENUM.FIXED_AMOUNT &&
                            formState.amount < 1000
                          ? 'Amount must be at least $10'
                          : null
                    }
                  });
                }
              }}
              value={formState.fkPaymentAmountType}
            />
          </AppFlexbox>
        </AppStack>

        <AppStack style={{ gap: 5 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>Due</AppText>
          <AppFlexbox
            style={{
              flex: 1,
              gap: 8,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {formState.fkPaymentDueType === PAYMENT_DUE_TYPE_ENUM.FIXED_DATE ? (
              <DatePickerInput
                clearable
                disabled={formState.loading}
                leftSection={<Calendar color="#000" size={16} />}
                minDate={
                  formState.fixedDueDate && formState.fixedDueDate <= new Date()
                    ? formState.fixedDueDate
                    : new Date()
                }
                onChange={(value) =>
                  setFormState({
                    ...formState,
                    errors: {},
                    fixedDueDate: value
                  })
                }
                placeholder="MM/DD/YYYY"
                required
                style={{ flex: 1 }}
                value={formState.fixedDueDate}
                valueFormat="MM/DD/YYYY"
              />
            ) : formState.fkPaymentDueType ===
              PAYMENT_DUE_TYPE_ENUM.IMMEDIATE ? (
              <CustomNumberInput
                allowNegative={false}
                disabled
                hideControls
                placeholder="---"
                required
                style={{ flex: 1 }}
              />
            ) : (
              <CustomNumberInput
                allowNegative={false}
                decimalScale={2}
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    dueValue: v
                  });
                }}
                placeholder="1"
                required
                style={{ flex: 1 }}
                value={formState.dueValue}
                withAsterisk={false}
              />
            )}

            <Select
              data={PAYMENT_DUE_TYPE_LIST.filter((item) =>
                isFirstInstallment
                  ? !item.hideOnFirstInstallment
                  : !item.hideOnSubsequentInstallments
              )}
              onChange={(v) => {
                if (v) {
                  setFormState({
                    ...formState,
                    fkPaymentDueType: v
                  });
                }
              }}
              value={formState.fkPaymentDueType}
            />
          </AppFlexbox>
        </AppStack>
      </AppStack>
    </ResponsiveModal>
  );
};

PaymentPlanAddInstallmentModal.propTypes = {
  installment: PropTypes.object,
  installments: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func
};

export default PaymentPlanAddInstallmentModal;
